// Vue
import { nextTick, ref, watch } from "vue";

// Dependencies
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";

// Types
import settingsSections, { SettingsSectionType } from "@/pages/ProjectSettings/settingsSections";

// Data Providers
import { useLocalization } from "@/providers/Localization";
import { ProjectWrapperType, useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { toProjectWrapper } from "@/dataProviders/ProjectWrapper/services/projectWrapperFromHasura";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { GetProjectSettingsDocument, type GetProjectSettingsQuery, type GetProjectSettingsQueryVariables } from "@/graphql";
import { useProject } from "@/stores/project/project.store";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { useStageDefaultEmissions } from "@/dataProviders/StageDefaultEmissions";

// Utils
import { services as BuildingProjectServices } from "@/features/BuildingProject";
import useInstallationEmissionFactors from "./InstallationEmissionFactorsProvider";
import { useBreadcrumb } from "@/features/Breadcrumb";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";
import payloadChanged from "../services/payloadChanged";
import { editedSince } from "../services/editedSince";
import { followSpeckleOwnedState } from "../services/speckleAttachState";
import { modelSignature } from "../services/modelSignature";
import { pendingRemovals, removalRefusalMessages, settlePendingModelRemovals } from "../services/pendingModelRemovals";
import { deleteEmptySpeckleModel } from "@/features/Speckle/speckleStreamModels";

const useEditBuildingProjectWizard = defineStore("EditBuildingProjectWizardProvider", () => {
  // Data

  const firstStep = "PROJECT_DETAILS";

  // Providers

  const localization = useLocalization();
  const projectWrapperProvider = useProjectWrapper();
  const installationEmissionFactors = useInstallationEmissionFactors();
  const stageDefaultEmissions = useStageDefaultEmissions();
  const breadcrumb = useBreadcrumb();

  // Refs

  const wizardFlow = ref<Array<SettingsSectionType>>();
  const projectWrapper = ref<ProjectWrapperType>({
    project: {} as ProjectWrapperType["project"],
    models: [] as ProjectWrapperType["models"],
  });
  const validSteps = ref<string[]>([]);
  const currentStep = ref<string>(firstStep);
  const isSaving = ref<boolean>(false);
  const mounted = ref<boolean>(false);

  // The project as last saved from the server, kept in memory so discard is instant. Deep-cloned, not
  // referenced — the live object is the one being edited, so a shallow copy would track every keystroke.
  const savedProject = ref<ProjectWrapperType["project"] | null>(null);

  // Whole rows, not just active names: creating a model is a pending edit too, and a name-only baseline
  // would miss one created then toggled off before save. Null means "unknown" — writes read that as "send it".
  const savedModels = ref<ProjectWrapperType["models"] | null>(null);

  /** True only on the fallback discard path (no snapshot to restore from) — the instant path never sets this. */
  const isDiscarding = ref<boolean>(false);

  // Set when init() throws, so the spinner (gated on `mounted`) doesn't spin forever on a failed load.
  // Kept separate from `mounted` rather than a tri-state — templates check the two as separate questions.
  const initFailed = ref<boolean>(false);

  // Getters

  const getConditionalWizardFlow = () => {
    if (!wizardFlow.value) return [];
    return wizardFlow.value.filter((step) =>
      step.options.projectCondition ? step.options.projectCondition(getProjectWrapper().project) : true
    );
  };

  const getWizardStepFromType = (id: string) => {
    if (!wizardFlow.value) return {} as SettingsSectionType;
    return wizardFlow.value.find((step) => step.id === id) || ({} as SettingsSectionType);
  };

  const getMounted = () => {
    return mounted.value;
  };

  const getInitFailed = () => {
    return initFailed.value;
  };

  const getProjectWrapper = () => {
    return projectWrapper.value;
  };

  const getIsSaving = () => {
    return isSaving.value;
  };

  const getIsDiscarding = () => {
    return isDiscarding.value;
  };

  // The last saved copy, for surfaces that need what CHANGED rather than what is valid. Null (before
  // load, or after a failed clone) means "cannot tell", not "nothing changed" — callers must read it that way.
  const getSavedProject = () => savedProject.value;

  // Setters

  const setWizardFlow = (newWizardFlow: Array<SettingsSectionType>) => {
    wizardFlow.value = newWizardFlow;
  };

  // Unique type values
  // Uses the unique enum type to identify steps so indexes are not used
  const setCurrentStep = (newCurrentStep: string) => {
    currentStep.value = newCurrentStep;
  };

  const setMounted = (newValue: boolean) => {
    mounted.value = newValue;
  };

  const setIsSaving = (newValue: boolean) => {
    isSaving.value = newValue;
  };

  const setValidSteps = (newValue: string[]) => {
    validSteps.value = newValue;
  };

  // Methods

  const handleStepValidation = async (step: string) => {
    const wizardStep = getWizardStepFromType(step);

    if (!wizardStep.valid) return true;

    return wizardStep.valid(getProjectWrapper().project, getSavedProject());
  };

  const handleAllStepsValidation = async () => {
    const allSteps = getConditionalWizardFlow();

    const validSteps: string[] = [];
    for (const wizardStep of allSteps) {
      const valid = await handleStepValidation(wizardStep.id);
      if (valid) {
        validSteps.push(wizardStep.id);
      }
    }

    // Set validSteps
    setValidSteps(validSteps);
  };

  // Validates, writes and re-reads — everything a save does except deciding where you end up after.
  // Returns whether the write happened, so each host (wizard vs settings page) can branch on the result.
  const runSave = async (): Promise<boolean> => {
    // Guards against saving while previewing a revision — the B6 step shows the previewed revision's
    // saved values, and a save would push those over the live head. Both hosts also disable the button.
    if (useProjectRevisionsStore().isPreviewing(getProjectWrapper().project?.id ?? "")) {
      useToast().error("You're viewing an older revision. Go back to the current revision to make changes.");
      return false;
    }

    // Start isSaving. Released in the `finally` below, which is the only place that clears it — a
    // failed write used to leave it set, and the Save button spins, disabled, until a reload.
    setIsSaving(true);

    try {
      const allSteps = getConditionalWizardFlow();

      // Validate all steps
      await handleAllStepsValidation();

      if (validSteps.value.length !== allSteps.length) {
        useToast().error("Some fields are missing input");
        return false;
      }

      const projectId = getProjectWrapper().project.id;

      // Settled after the validation gate (a refused save deletes nothing from Speckle) and before the
      // write fan-out (a refusal clears the row's flag before modelSignature reads it in updateBuildingProject).
      const streamId = getProjectWrapper().project.streamId || "";
      const refusals = await settlePendingModelRemovals(getProjectWrapper().models, streamId, deleteEmptySpeckleModel);
      for (const message of removalRefusalMessages(refusals)) useToast().warning(message);

      // Only writes whose data actually changed — an ungated write still logs its own project_version
      // row. `saveEnabledStages` isn't gated here because it already diffs against the working state itself.
      const writes: Array<Promise<unknown>> = [
        // The snapshot rides along as the baseline so the service can skip any write whose payload hasn't
        // changed. Null (failed clone) makes it write everything instead.
        BuildingProjectServices.updateBuildingProject(getProjectWrapper(), savedProject.value, savedModels.value),
        // Persist the per-stage standard value toggles
        stageDefaultEmissions.saveEnabledStages(projectId),
      ];

      // Pass this wizard's own list and project explicitly, not the provider's — a background refresh may
      // have swapped the wrapper object since init(), and this must save what the user is looking at.
      const transportTypes = getProjectWrapper().project.transportTypes;
      if (payloadChanged(transportTypes, savedProject.value?.transportTypes)) {
        writes.push(projectWrapperProvider.insertProjectTransport(transportTypes, projectId));
      }

      // Insert Installation machinery fuel emission factors
      if (installationEmissionFactors.hasUnsavedChanges()) {
        writes.push(installationEmissionFactors.save(projectId));
      }

      await Promise.all(writes);

      // Forced, and the reference re-taken after: each refresh builds a NEW wrapper object, so a host that
      // stays on the page would otherwise keep pointing at the pre-save one.
      await projectWrapperProvider.refreshProjectWrapper(projectId, true, "hasura");
      projectWrapper.value = projectWrapperProvider.getProjectWrapper();
      // The dashboard reads the project from its own store; hand it what was just saved, or it shows the
      // pre-save status until a reload.
      useProject().applySavedProject(getProjectWrapper().project);

      // Marked rather than fetched — an unforced fetch no-ops on an already-initialised dashboard store,
      // which left stale pre-save numbers. Marked after the writes, so a failed save marks nothing.
      useDashboard().invalidateDashboard(projectId);

      // What a discard now reverts to. Without this the snapshot would still hold the pre-save values,
      // so Discard after a Save would undo the save on every field that has a write rail.
      await captureSavedProject();

      // Toast
      useToast().success("The project has been updated!");

      return true;
    } catch (error: unknown) {
      // "may not have saved", not "was not saved": the writes run concurrently, so others may have landed
      // by the time one rejects — claiming nothing changed would be the one thing we can't know for sure.
      Sentry.captureException(error);
      useToast().error("Could not save the project. Some sections may not have saved — try again.");

      // Deliberately no captureSavedProject() here: the snapshot stays at the last CONFIRMED save, so
      // Discard still reverts to a real state and the next save re-sends writes that may not have landed.
      return false;
    } finally {
      // Stop isSaving
      setIsSaving(false);
    }
  };

  const handleSaveInPlace = async (): Promise<boolean> => runSave();

  // Deep-copies via JSON, not structuredClone, which throws on this Vue reactive Proxy; exact for this
  // DTO today, but would need to change if the project ever gains a real Date field.
  const cloneProject = (project: ProjectWrapperType["project"]): ProjectWrapperType["project"] =>
    JSON.parse(JSON.stringify(project)) as ProjectWrapperType["project"];

  // Awaits nextTick() first: callers just replaced the wrapper, so a synchronous snapshot would diff
  // against fields the step components haven't normalized yet, marking untouched sections as edited.
  const captureSavedProject = async () => {
    await nextTick();
    try {
      savedProject.value = cloneProject(getProjectWrapper().project);
      savedModels.value = cloneModels(getProjectWrapper().models);
    } catch (error: unknown) {
      savedProject.value = null;
      savedModels.value = null;
      Sentry.captureException(error);
    }
  };

  /** Through JSON for the reason `cloneProject` is: the rows are reached through a reactive proxy. */
  const cloneModels = (models: ProjectWrapperType["models"]): ProjectWrapperType["models"] =>
    JSON.parse(JSON.stringify(models)) as ProjectWrapperType["models"];

  // Covers state fingerprint can't see (standard values, machinery-fuel factors, active models — none
  // live on the project). Keep in sync with runSave's own gates or the rail and the save can disagree.
  const hasExternalEdits = (stepId: string): boolean => {
    switch (stepId) {
      case "ALLOWANCES":
        return stageDefaultEmissions.hasEnabledStageEdits();
      case "INSTALLATION":
        return installationEmissionFactors.hasEdits();
      case "MODELS": {
        const saved = savedModels.value;
        if (saved === null) return false;
        // Which models exist as well as which are on: a model created here is unsaved until the page
        // is saved, and a name-only signature would leave the rail dark for it.
        return editedSince(modelSignature(getProjectWrapper().models), modelSignature(saved));
      }
      default:
        return false;
    }
  };

  // Mutates the shared ProjectWrapper object in place (delete extra keys, then Object.assign) rather
  // than replacing it — other surfaces hold the same reference. Models use splice for the same reason.
  const restoreSavedProjectLocally = () => {
    if (!savedProject.value) return false;

    const live = getProjectWrapper().project as unknown as Record<string, unknown>;
    const saved = cloneProject(savedProject.value) as unknown as Record<string, unknown>;

    for (const key of Object.keys(live)) {
      if (!(key in saved)) delete live[key];
    }

    Object.assign(live, saved);

    if (savedModels.value) {
      const models = getProjectWrapper().models;
      models.splice(0, models.length, ...cloneModels(savedModels.value));
    }

    return true;
  };

  // Forced: the provider otherwise skips a refresh for a project it already holds. The reference is
  // re-taken after, since each refresh builds a new wrapper object the flow must point at instead.
  const restoreSavedProject = async (projectId: string) => {
    await projectWrapperProvider.forceUpdateProjectWrapperAndAdditionalInfo(projectId, "hasura");

    projectWrapper.value = projectWrapperProvider.getProjectWrapper();
  };

  // Deliberately stays MOUNTED across the refetch (no reload flash) and only restores project/models —
  // standard-value and machinery-fuel edits (stageDefaultEmissions, installationEmissionFactors) persist until reload.
  const handleDiscardInPlace = async () => {
    const projectId = getProjectWrapper().project?.id;

    if (!projectId) return;

    if (restoreSavedProjectLocally()) {
      // Not awaited for the sake of a fetch — `handleAllStepsValidation` only runs the step
      // predicates — so the marks in the rail land with the restored fields.
      await handleAllStepsValidation();

      useToast().success("Your changes have been discarded");
      return;
    }

    isDiscarding.value = true;

    try {
      await restoreSavedProject(projectId);
      await handleAllStepsValidation();

      useToast().success("Your changes have been discarded");
    } catch (error: unknown) {
      Sentry.captureException(error);
      useToast().error("Could not discard your changes. Try reloading the page.");
    } finally {
      isDiscarding.value = false;
    }
  };

  const loadFlow = async () => {
    // Clear per-stage standard-value state from any previous project; `asSaved: true` also resets the
    // baseline, or an empty map reads as an edit against the previous project's baseline.
    stageDefaultEmissions.setEnabledStages({}, { asSaved: true });
    stageDefaultEmissions.setBuildingTypeFilter(null);

    // Get route
    const route = useRoute();

    // No project id in the URL is a broken arrival (both hosts live under `/project/:id`), not a state
    // to sit in — throw so the screen reports failure instead of spinning forever.
    if (!route?.params?.id) throw new Error("Edit wizard init: no project id in the route");

    // Forced + "hasura" source, deliberately: unforced returns whatever the layout already cached
    // (REST shape, no speckleModelId), which breaks this page's project_model row edits.
    await Promise.all([
      projectWrapperProvider.refreshProjectWrapper(String(route?.params?.id), true, "hasura"),
      projectWrapperProvider.refreshProjectAdditionalInfoWrapper(String(route?.params?.id)),
    ]);

    // Get project from provider
    projectWrapper.value = projectWrapperProvider.getProjectWrapper();

    // Clear any leftover pendingRemoval before the baseline below is captured — a stale flag survives
    // into both sides of modelSignature, so the save would write nothing while the UI shows it leaving.
    for (const model of pendingRemovals(getProjectWrapper().models)) model.pendingRemoval = false;

    // The copy a discard restores from, taken before anything can be edited.
    await captureSavedProject();

    // Update breadcrumb with actual project name now that data is loaded
    if (getProjectWrapper()?.project?.projectName) {
      const currentBreadcrumb = [...breadcrumb.items];
      if (currentBreadcrumb[1]) {
        currentBreadcrumb[1] = {
          ...currentBreadcrumb[1],
          label: getProjectWrapper().project.projectName,
        };
        breadcrumb.set(currentBreadcrumb);
      }
    }

    // Guarded (else "undefined" showed for projects with no name) and matches the router's own title
    // format — set here because the router's afterEach runs before this fetch resolves and has no name yet.
    const loadedProjectName = getProjectWrapper().project?.projectName;
    if (loadedProjectName) document.title = `${loadedProjectName} - Real-Time LCA`;

    // Set localization country from baseInfo
    localization.setCountryFromLongName(getProjectWrapper()?.project?.buildingAddress?.country || "Denmark");

    // The settings page's sections, in the registry's own order. Which of them apply to this project is
    // each section's projectCondition, read by getConditionalWizardFlow.
    setWizardFlow(settingsSections());

    // No step is opened on screen here; `currentStep` stays as the flow's internal cursor, read by
    // `handleAllStepsValidation` and the step list.
    setCurrentStep(getConditionalWizardFlow()[0]?.id);

    // Validate all steps
    await handleAllStepsValidation();

    // Set mounted
    setMounted(true);
  };

  // Folds only Speckle-owned fields (followSpeckleOwnedState) instead of replacing the wrapper, so
  // unsaved edits elsewhere survive; not init() — callers are event handlers, so projectId comes as an arg.
  const rebaseOnServer = async (projectId: string) => {
    await projectWrapperProvider.forceUpdateProjectWrapperAndAdditionalInfo(projectId, "hasura");
    const fresh = projectWrapperProvider.getProjectWrapper();
    if (!projectWrapper.value) {
      projectWrapper.value = fresh;
    } else {
      followSpeckleOwnedState(projectWrapper.value, { project: savedProject.value, models: savedModels.value }, fresh, {
        project: cloneProject,
        models: cloneModels,
      });
    }
    useProject().applySavedProject(fresh.project);
    await handleAllStepsValidation();
  };

  // Merges only Speckle-owned columns and newly imported rows by name; never flips `active`.
  // `reloadModels` would replace the list and wrongly move the baseline after a background sync.
  const refreshModelSyncState = async (projectId: string) => {
    const fresh = await readProjectFromHasura(projectId);
    followSpeckleOwnedState(getProjectWrapper(), { project: savedProject.value, models: savedModels.value }, fresh, {
      project: cloneProject,
      models: cloneModels,
    });
    await handleAllStepsValidation();
  };

  const readProjectFromHasura = async (projectId: string): Promise<ProjectWrapperType> => {
    const result = await gaiupApolloClient.query<GetProjectSettingsQuery, GetProjectSettingsQueryVariables>({
      query: GetProjectSettingsDocument,
      fetchPolicy: "network-only",
      variables: { projectId },
    });
    const fresh = result.data ? toProjectWrapper(result.data) : null;
    if (!fresh) throw new Error("The project's models could not be re-read from Hasura.");
    return fresh;
  };

  const init = async () => {
    initFailed.value = false;

    try {
      // Keep this the first call in init(): useRoute() inside loadFlow() only resolves in the caller's setup tick.
      await loadFlow();
    } catch (error: unknown) {
      initFailed.value = true;
      Sentry.captureException(error);
      useToast().error("Could not load this project's settings. Try reloading the page.");
    }
  };

  const reset = () => {
    validSteps.value = [];
    currentStep.value = firstStep;
    isSaving.value = false;
    mounted.value = false;
    initFailed.value = false;

    projectWrapper.value = {
      project: {} as ProjectWrapperType["project"],
      models: [] as ProjectWrapperType["models"],
    };
  };

  // Watchers

  // The section list does not depend on the country any more, but which sections apply does.
  watch(localization.getCountry, async () => {
    if (getProjectWrapper().project?.buildingAddress ?? false) {
      // Validate all steps
      await handleAllStepsValidation();
    }
  });

  // Returns

  return {
    getConditionalWizardFlow,
    getMounted,
    getInitFailed,
    getProjectWrapper,
    getSavedProject,
    hasExternalEdits,
    getIsSaving,
    getIsDiscarding,

    handleSaveInPlace,
    handleDiscardInPlace,
    rebaseOnServer,
    refreshModelSyncState,

    init,
    reset,
  };
});

export default useEditBuildingProjectWizard;
export type { ProjectWrapperType };
